import { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AudienceView,
  BLANK_DRAFT,
  MessagesView,
  RECALL_DRAFT,
  TestStartView,
  recallById,
  startingSteps,
  stepsAreUntouched,
} from '@elva/ui';
import type { RecallDraft } from '@elva/ui';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';

/**
 * The recall builder (spec 21). Three pages over one draft.
 *
 * **Everything saves as a draft as you go**, so leaving is always safe — which is
 * why the draft lives above the three pages rather than inside any one of them.
 * The same three components serve `/recall/new/*` and `/recall/<id>/*`: **one
 * component per page for add and edit, because if they are two they drift.**
 */
const draftStore: { current: RecallDraft; loadedFor: string | undefined } = {
  // A NEW recall starts blank. It used to start from the worked hygiene example,
  // which is why every new recall arrived named *Hygiene — 6 month* with four
  // hygiene steps whatever goal was picked (spec 23 §1.1, §1.4).
  current: { ...BLANK_DRAFT },
  loadedFor: undefined,
};

/** Called when the builder is entered at `/recall/new` rather than on an id. */
export function resetDraft(): void {
  if (draftStore.loadedFor === undefined) return;
  draftStore.current = { ...BLANK_DRAFT };
  draftStore.loadedFor = undefined;
}

/**
 * **Edit opens the builder filled in** (amendment 22 §4) — the row's name, goal
 * and steps, on top of the worked audience. Before this, Edit and Setup both
 * opened an empty sequence page for every recall.
 *
 * A draft is loaded once per id: re-reading it on every render would throw away
 * whatever was being typed.
 */
export function loadDraftFor(id: string): RecallDraft {
  if (draftStore.loadedFor === id) return draftStore.current;
  const row = recallById(id);
  if (row === undefined) return draftStore.current;
  const steps = row.steps.length > 0 ? startingSteps(row.goal).slice(0, row.steps.length) : [];
  draftStore.current = {
    ...RECALL_DRAFT,
    name: row.name,
    goal: row.goal,
    // A draft has nobody in it yet, so it carries no audience result either.
    ...(row.state === 'draft'
      ? { readback: undefined, count: undefined, patients: [], leftOut: [] }
      : {}),
    steps: steps.map((s, i) => ({ ...s, channel: row.steps[i] ?? s.channel })),
  } as RecallDraft;
  draftStore.loadedFor = id;
  return draftStore.current;
}

function useDraft() {
  const [draft, setDraft] = useState<RecallDraft>(draftStore.current);
  const update = useCallback((next: RecallDraft) => {
    const goal = next.goal;
    const previous = draftStore.current.goal;
    const goalChanged = goal !== undefined && goal !== previous;
    // Picking a goal loads THAT goal's starting steps — a new recall does not
    // start empty, because an empty editor is where a practice stalls. The old
    // test was `steps.length === 0`, so a loyalty recall kept the four hygiene
    // steps it was seeded with. **Steps somebody has already written are kept**
    // (spec 21 §3.2); only untouched ones are replaced.
    const withSteps =
      goalChanged && stepsAreUntouched(next.steps, previous) ? { ...next, steps: startingSteps(goal) } : next;
    draftStore.current = withSteps;
    setDraft(withSteps);
  }, []);
  return [draft, update] as const;
}

/** Where the builder lives, for both new and existing recalls. */
function base(id: string | undefined): string {
  return id === undefined ? '/recall/new' : `/recall/${id}`;
}

export function RecallAudiencePage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const { id } = useParams();
  if (id === undefined) resetDraft();
  else loadDraftFor(id);
  const [draft, setDraft] = useDraft();
  return (
    <AudienceView
      draft={draft}
      editing={id !== undefined}
      onChange={setDraft}
      onContinue={() => { void navigate(`${base(id)}/messages`); }}
      onCancel={() => { void navigate('/recall'); }}
    />
  );
}

export function RecallMessagesPage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const { id } = useParams();
  // A fix link on page 3 names a step, and `?step=` is how it survives the
  // navigation — without it the page opened step 1 whatever the link said (§4.2).
  const [params] = useSearchParams();
  const wanted = params.get('step');
  if (id === undefined) resetDraft();
  else loadDraftFor(id);
  const [draft, setDraft] = useDraft();
  return (
    <MessagesView
      draft={draft}
      initialStepId={wanted ?? undefined}
      onChange={setDraft}
      onBack={() => { void navigate(`${base(id)}/audience`); }}
      onContinue={() => { void navigate(`${base(id)}/test`); }}
      onSaveDraft={() => { void navigate('/recall'); }}
    />
  );
}

export function RecallTestPage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const { id } = useParams();
  if (id === undefined) resetDraft();
  else loadDraftFor(id);
  const [draft, setDraft] = useDraft();
  return (
    <TestStartView
      draft={draft}
      onChange={setDraft}
      running={id !== undefined}
      onBack={() => { void navigate(`${base(id)}/messages`); }}
      onEditStep={(stepId) => { void navigate(`${base(id)}/messages?step=${stepId}`); }}
      onSaveDraft={() => { void navigate('/recall'); }}
      onStart={() => { void navigate('/recall'); }}
    />
  );
}
